/**
 * IndexedDB wrapper (T-02) — the durable local store backing the outbox.
 * Five object stores: the four design §1.1 capture stores (`outbox`, `blobs`,
 * `ordinals`, `meta`) plus BUI-342's owner-bound `usage_events` queue.
 * `ordinals` (T-15 dedup ordering) and `meta` (T-24 device config) are not
 * populated by this batch's tasks, but the stores exist now so later
 * batches don't need a schema migration to add them.
 */

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { BlobRecord, OutboxRecord, OutboxState } from "./types";
import { emitOutboxChanged } from "./changes";

interface OrdinalRecord {
  canonical_ref: string;
  cursor: number;
}

interface MetaRecord {
  key: string;
  value: unknown;
}

export interface QueuedUsageEvent {
  owner_principal: string;
  tenant_id: string;
  actor_id: string;
  delivery_state: "pending" | "rejected";
  rejection_code?: string;
  client_event_id: string;
  event_kind:
    | "app_foreground"
    | "project_selected"
    | "capture_started"
    | "capture_saved"
    | "capture_synced"
    | "report_viewed"
    | "report_filed"
    | "report_downloaded"
    | "action_viewed"
    | "action_mutated"
    | "decision_viewed"
    | "decision_mutated"
    | "feedback_submitted"
    | "bug_submitted"
    | "foreground_interval";
  occurred_at: string;
  app_release: string;
  project_ref?: string;
  foreground_seconds?: number;
}

interface MarkOutboxDB extends DBSchema {
  outbox: {
    key: string; // capture_id
    value: OutboxRecord;
    indexes: { "by-state": string };
  };
  blobs: {
    key: [string, number]; // [capture_id, part_index]
    // Bytes since 2026-10-02 (see toStoredPart); rows written earlier hold a Blob.
    value: StoredBlobRecord | BlobRecord;
    indexes: { "by-capture": string };
  };
  ordinals: {
    key: string; // canonical_ref
    value: OrdinalRecord;
  };
  meta: {
    key: string;
    value: MetaRecord;
  };
  usage_events: {
    key: [string, string];
    value: QueuedUsageEvent;
    indexes: {
      "by-occurred": string;
      "by-owner-occurred": [string, string];
    };
  };
}

const DB_NAME = "mark-outbox";
const DB_VERSION = 3;

let dbPromise: Promise<IDBPDatabase<MarkOutboxDB>> | null = null;

function announceUsageQueued(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("mark-usage-queued"));
}

export function openOutboxDB(): Promise<IDBPDatabase<MarkOutboxDB>> {
  if (!dbPromise) {
    dbPromise = openDB<MarkOutboxDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, _transaction) {
        if (oldVersion < 1) {
          const outbox = db.createObjectStore("outbox", { keyPath: "capture_id" });
          outbox.createIndex("by-state", "state");

          const blobs = db.createObjectStore("blobs", {
            keyPath: ["capture_id", "part_index"],
          });
          blobs.createIndex("by-capture", "capture_id");

          db.createObjectStore("ordinals", { keyPath: "canonical_ref" });
          db.createObjectStore("meta", { keyPath: "key" });
        }
        if (oldVersion < 2) {
          const usage = db.createObjectStore("usage_events", {
            keyPath: ["owner_principal", "client_event_id"],
          });
          usage.createIndex("by-occurred", "occurred_at");
          usage.createIndex("by-owner-occurred", ["owner_principal", "occurred_at"]);
        } else if (oldVersion < 3) {
          // Rejected BUI-342 v2 rows carried no authenticated owner and cannot
          // safely be adopted. Replace that never-promoted store rather than
          // attributing its contents to whoever signs in next.
          db.deleteObjectStore("usage_events");
          const usage = db.createObjectStore("usage_events", {
            keyPath: ["owner_principal", "client_event_id"],
          });
          usage.createIndex("by-occurred", "occurred_at");
          usage.createIndex("by-owner-occurred", ["owner_principal", "occurred_at"]);
        }
      },
    });
  }
  return dbPromise;
}

/** Test-only: closes the current connection and drops the in-memory
 * handle cache WITHOUT deleting the underlying data — simulates an app
 * restart (kill + relaunch) so a mid-test assertion can prove a write
 * already made durable in one IDB transaction survives a dropped
 * connection. */
export async function _closeOutboxDBForTests(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
  }
  dbPromise = null;
}

/** Test-only: closes the current connection AND deletes the underlying
 * database, so the NEXT `openOutboxDB()` call starts from a genuinely
 * empty store — full between-test isolation (use in `beforeEach`; use
 * `_closeOutboxDBForTests` instead when a test needs data to survive the
 * simulated restart). */
export async function _resetOutboxDBForTests(): Promise<void> {
  await _closeOutboxDBForTests();
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve(); // best-effort in the test harness
  });
}

export async function putOutboxRecord(record: OutboxRecord): Promise<void> {
  const db = await openOutboxDB();
  await db.put("outbox", record);
  // Announce AFTER the write settles, so a subscriber that re-reads on this
  // signal observes the committed row (see changes.ts for why the store emits
  // rather than trusting every caller to refresh).
  emitOutboxChanged();
}

/**
 * An attachment as the blobs store holds it: the bytes and their type, never a
 * Blob. iPhone Safari cannot store a Blob or File in IndexedDB in some tabs
 * (Private Browsing, at least): a raw put fails with "UnknownError: Error
 * preparing Blob/File data", and inside the capture transaction the puts never
 * settle at all, so Save hung. Plain bytes store everywhere (traced on-device
 * 2026-10-02; copying a File into a new Blob, the 2026-10-01 fix, didn't help).
 */
type StoredBlobRecord = Omit<BlobRecord, "blob"> & { bytes: ArrayBuffer; blob_type: string };

/** Read the bytes BEFORE opening a transaction: awaiting inside one lets it auto-commit. */
async function toStoredPart(part: BlobRecord): Promise<StoredBlobRecord> {
  const { blob, ...rest } = part;
  return { ...rest, bytes: await blob.arrayBuffer(), blob_type: blob.type };
}

function fromStoredPart(row: StoredBlobRecord | BlobRecord): BlobRecord {
  if (!("bytes" in row)) return row;
  const { bytes, blob_type, ...rest } = row;
  return { ...rest, blob: new Blob([bytes], { type: blob_type }) };
}

/**
 * The ack-bound write (§1.3 AC-1g-22 acked-never-lost): the outbox record
 * and every one of its blob parts land in ONE IndexedDB transaction, and
 * the caller must await this promise (bound to the transaction's `complete`
 * event, never a network round-trip) before rendering the optimistic ack.
 */
export async function putOutboxRecordWithBlobsInOneTx(
  record: OutboxRecord,
  parts: BlobRecord[],
): Promise<void> {
  const storable = await Promise.all(parts.map(toStoredPart));
  const db = await openOutboxDB();
  const tx = db.transaction(["outbox", "blobs"], "readwrite");
  await Promise.all([
    tx.objectStore("outbox").put(record),
    ...storable.map((p) => tx.objectStore("blobs").put(p)),
  ]);
  await tx.done;
  // Emitted after tx.done — this is THE capture-commit path, and the one whose
  // missing refresh left the Daily Log empty after a save.
  emitOutboxChanged();
}

/** The accepted capture transition and its capture_synced fact are one local
 * durability boundary. A kill can observe both or neither, never delivered
 * without the deterministic usage event needed to account for that fact. */
export async function putDeliveredOutboxWithUsageInOneTx(
  record: OutboxRecord,
  usageEvent?: QueuedUsageEvent,
): Promise<void> {
  const db = await openOutboxDB();
  const stores: Array<"outbox" | "usage_events"> = usageEvent
    ? ["outbox", "usage_events"]
    : ["outbox"];
  const tx = db.transaction(stores, "readwrite");
  await tx.objectStore("outbox").put(record);
  if (usageEvent) await tx.objectStore("usage_events").put(usageEvent);
  await tx.done;
  emitOutboxChanged();
  if (usageEvent) announceUsageQueued();
}

export async function getOutboxRecord(capture_id: string): Promise<OutboxRecord | undefined> {
  const db = await openOutboxDB();
  return db.get("outbox", capture_id);
}

export async function getAllOutboxRecords(): Promise<OutboxRecord[]> {
  const db = await openOutboxDB();
  return db.getAll("outbox");
}

/** Every state but `delivered`: an entry that exists only in this browser's
 * storage and would be lost if the human abandoned it. Rejected entries count
 * too; they need a human to re-save or delete them, not a wait. */
const RECOVERABLE_STATES = ["draft", "held", "queued", "sending", "rejected"] as const;

/** Entries that exist only on this device. The iOS install gate counts them:
 * Safari's storage is not the installed app's, so these can only be finished
 * from the browser they were saved in. */
export async function countRecoverableOutboxRecords(): Promise<number> {
  const db = await openOutboxDB();
  const counts = await Promise.all(
    RECOVERABLE_STATES.map((state) => db.countFromIndex("outbox", "by-state", state)),
  );
  return counts.reduce((a, b) => a + b, 0);
}

/** The iOS install gate's explicit "discard" exit: deletes every recoverable
 * entry and its attachments in one transaction, and returns how many went.
 * Delivered entries are untouched. Only a human confirming on the gate calls
 * this; nothing else may throw captures away in bulk. */
export async function discardRecoverableOutboxRecords(): Promise<number> {
  const db = await openOutboxDB();
  const tx = db.transaction(["outbox", "blobs"], "readwrite");
  const outbox = tx.objectStore("outbox");
  const blobs = tx.objectStore("blobs");
  const ids = (
    await Promise.all(RECOVERABLE_STATES.map((state) => outbox.index("by-state").getAllKeys(state)))
  ).flat();
  for (const capture_id of ids) {
    const parts = await blobs.index("by-capture").getAllKeys(capture_id);
    await Promise.all([outbox.delete(capture_id), ...parts.map((key) => blobs.delete(key))]);
  }
  await tx.done;
  emitOutboxChanged();
  return ids.length;
}

export async function getOutboxByState(state: OutboxState): Promise<OutboxRecord[]> {
  const db = await openOutboxDB();
  return db.getAllFromIndex("outbox", "by-state", state);
}

export async function putBlobRecord(part: BlobRecord): Promise<void> {
  const storable = await toStoredPart(part);
  const db = await openOutboxDB();
  await db.put("blobs", storable);
}

export async function getBlobsForCapture(capture_id: string): Promise<BlobRecord[]> {
  const db = await openOutboxDB();
  const rows = await db.getAllFromIndex("blobs", "by-capture", capture_id);
  return rows.map(fromStoredPart);
}

export async function getMeta(key: string): Promise<unknown | undefined> {
  const db = await openOutboxDB();
  const rec = await db.get("meta", key);
  return rec?.value;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await openOutboxDB();
  await db.put("meta", { key, value });
}

const MAX_QUEUED_USAGE_EVENTS = 1000;

export async function putQueuedUsageEvent(event: QueuedUsageEvent): Promise<void> {
  const db = await openOutboxDB();
  const tx = db.transaction("usage_events", "readwrite");
  const store = tx.objectStore("usage_events");
  await store.put(event);
  const events = await store.index("by-owner-occurred").getAll(
    IDBKeyRange.bound(
      [event.owner_principal, ""],
      [event.owner_principal, "\uffff"],
    ),
  );
  for (const expired of events.slice(0, Math.max(0, events.length - MAX_QUEUED_USAGE_EVENTS))) {
    await store.delete([expired.owner_principal, expired.client_event_id]);
  }
  await tx.done;
  announceUsageQueued();
}

export async function getAllUsageEventsForOwner(
  ownerPrincipal: string,
): Promise<QueuedUsageEvent[]> {
  const db = await openOutboxDB();
  return db.getAllFromIndex(
    "usage_events",
    "by-owner-occurred",
    IDBKeyRange.bound([ownerPrincipal, ""], [ownerPrincipal, "\uffff"]),
  );
}

export async function getQueuedUsageEvents(
  ownerPrincipal: string,
  limit = 50,
): Promise<QueuedUsageEvent[]> {
  const events = await getAllUsageEventsForOwner(ownerPrincipal);
  return events
    .filter((event) => event.delivery_state === "pending")
    .slice(0, Math.max(0, Math.min(limit, 50)));
}

export async function deleteAcknowledgedUsageEvents(
  ownerPrincipal: string,
  clientEventIds: string[],
): Promise<void> {
  if (clientEventIds.length === 0) return;
  const db = await openOutboxDB();
  const tx = db.transaction("usage_events", "readwrite");
  for (const id of clientEventIds) {
    const key: [string, string] = [ownerPrincipal, id];
    const event = await tx.store.get(key);
    if (event?.owner_principal === ownerPrincipal) await tx.store.delete(key);
  }
  await tx.done;
}

export async function rejectQueuedUsageEvents(
  ownerPrincipal: string,
  rejections: Array<{ client_event_id: string; error: string }>,
): Promise<void> {
  if (rejections.length === 0) return;
  const db = await openOutboxDB();
  const tx = db.transaction("usage_events", "readwrite");
  for (const rejection of rejections) {
    const key: [string, string] = [ownerPrincipal, rejection.client_event_id];
    const event = await tx.store.get(key);
    if (event?.owner_principal !== ownerPrincipal || event.delivery_state !== "pending") continue;
    await tx.store.put({
      ...event,
      delivery_state: "rejected",
      rejection_code: rejection.error,
    });
  }
  await tx.done;
}
